import * as React from 'react'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation } from '@tanstack/react-query'
import { requireAuth } from '@/lib/auth'
import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Search,X,RefreshCw,MoreHorizontal,Pencil,Trash2,Star,Printer as PrinterIcon } from 'lucide-react'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Select,SelectContent,SelectItem,SelectTrigger,SelectValue } from '@/components/ui/select'
import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import { Skeleton } from '@/components/ui/skeleton'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import { queryKeys } from '@/lib/query-keys'
import { getPrinters,getPrintAgents,testPrinter } from '@/features/devices/api'
import { useSelectedShopId } from '@/features/shared'
import type { Printer } from '@/types'
import { PrinterTypeBadge } from '@/features/devices/printer-type-badge'
import { PrinterStatusIndicator } from '@/features/devices/printer-status-indicator'
import { PrinterDetailCard } from '@/features/devices/printer-detail-card'
import { toast } from 'sonner'

const DeletePrinterDialog = React.lazy(() => import('@/features/devices/delete-printer-dialog').then(m => ({ default: m.DeletePrinterDialog })))
const CreatePrinterDialog = React.lazy(() => import('@/features/devices/create-printer-dialog').then(m => ({ default: m.CreatePrinterDialog })))

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  limit: z.number().int().min(1).catch(20),
  search: z.string().catch(''),
  type: z.enum(['RECEIPT', 'LABEL', 'PDF', '']).catch(''),
})

let suppressedPrintersAutoOpenSignature: string | null = null

export const Route = createFileRoute('/_dashboard/devices/printers')({
  validateSearch: searchSchema,
  beforeLoad: requireAuth(),
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  component: PrintersLayout,
})

function PrintersLayout() {
  return <Outlet />
}

export function PrintersPage({ selectedId }: { selectedId: string | undefined }) {
  const searchParams = useSearch({ from: Route.id })
  const navigate = useNavigate()
  const shopId = useSelectedShopId()

  const [searchTerm, setSearchTerm] = React.useState(searchParams.search || '')
  
  const [editingPrinter, setEditingPrinter] = React.useState<Printer | null>(null)
  const [deletingPrinter, setDeletingPrinter] = React.useState<Printer | null>(null)
  const [testingPrinterId, setTestingPrinterId] = React.useState<string | null>(null)

  const testMutation = useMutation({
    mutationFn: (id: string) => testPrinter(id),
    onMutate: (id) => setTestingPrinterId(id),
    onSuccess: () => {
      toast.success('Test print queued successfully')
      setTestingPrinterId(null)
    },
    onError: () => {
      toast.error('Failed to queue test print')
      setTestingPrinterId(null)
    },
  })

  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const page = searchParams.page
  const limit = searchParams.limit
  const typeFilter = searchParams.type
  const pageSignature = `${shopId ?? 'no-shop'}:${page}:${limit}:${searchParams.search}:${typeFilter}`

  const printersQuery = useQuery({
    queryKey: queryKeys.printers(shopId ?? '', { page, limit, search: searchParams.search, type: typeFilter }),
    queryFn: () => getPrinters(shopId!, { 
      page, 
      limit, 
      search: searchParams.search || undefined, 
      type: typeFilter || undefined 
    }),
    enabled: !!shopId,
    refetchInterval: 10_000,
  })

  function handleRefresh() {
    setIsRefreshing(true)
    void printersQuery.refetch().then(() => setTimeout(() => setIsRefreshing(false), 600))
  }

  const { data: agentsData } = useQuery({
    queryKey: queryKeys.printAgents(shopId ?? '', { limit: 100 }),
    queryFn: () => getPrintAgents(shopId!, { limit: 100 }),
    enabled: !!shopId,
  })

  const agentMap = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const a of agentsData?.data ?? []) {
      map.set(a.id, a.name)
    }
    return map
  }, [agentsData])

  const printers = React.useMemo(
    () => printersQuery.data?.data ?? [],
    [printersQuery.data?.data],
  )
  const isLoading = printersQuery.isLoading
  const meta = printersQuery.data?.meta ?? { total: 0, page: 1, limit: 20, totalPages: 1 }

  const currentPageHasSelectedPrinter = React.useMemo(
    () => !!selectedId && printers.some((printer) => printer.id === selectedId),
    [printers, selectedId],
  )
  const effectiveSelectedId = React.useMemo(() => {
    if (currentPageHasSelectedPrinter) return selectedId
    if (isLoading || printers.length === 0) return undefined
    if (!selectedId && suppressedPrintersAutoOpenSignature === pageSignature) return undefined
    return printers[0].id
  }, [currentPageHasSelectedPrinter, isLoading, pageSignature, printers, selectedId])
  const selectedPrinter = React.useMemo(
    () => printers.find((p) => p.id === effectiveSelectedId),
    [printers, effectiveSelectedId]
  )

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    navigate({
      to: '/devices/printers',
      search: (prev) => ({ ...prev, page: 1, search: searchTerm || undefined }),
    })
  }

  const handleTypeChange = (val: string) => {
    const typeVal = (val === 'ALL' ? '' : val) as '' | 'RECEIPT' | 'LABEL' | 'PDF'
    navigate({
      to: '.',
      search: (prev) => ({ ...prev, page: 1, type: typeVal }),
      replace: true,
    })
  }

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/devices/printers/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }
      navigate({ to: '/devices/printers', search: (prev) => ({ ...prev }) })
    },
    [navigate]
  )

  const closeSelectedPrinter = React.useCallback(() => {
    suppressedPrintersAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/devices/printers')) {
          suppressedPrintersAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  return (
    <div className="space-y-6">
      <PageBreadcrumb />

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Printers</h2>
          <p className="text-muted-foreground">Manage your store's printers and their connections.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <form onSubmit={handleSearch} className="relative flex-1 max-w-sm group">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search printers..."
            className="pl-9 pr-8"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearchTerm('')
                navigate({ to: '/devices/printers', search: (prev) => ({ ...prev, page: 1, search: '' }) })
              }}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </form>
        
        <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={handleRefresh}>
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </Button>

        <Select value={typeFilter || 'ALL'} onValueChange={handleTypeChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All Types</SelectItem>
            <SelectItem value="RECEIPT">Receipt</SelectItem>
            <SelectItem value="LABEL">Label</SelectItem>
            <SelectItem value="PDF">PDF</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col md:flex-row gap-6 items-start">
        <div className={`flex-1 min-w-0 transition-all duration-300 ${!isLoading && printers.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'}`}>
          {!isLoading && printers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
              <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                <img src="/misc/logo.png" alt="Rootpoint Logo" className="w-full h-full object-contain" />
              </div>
              <div className="flex flex-col gap-4 items-center">
                <p className="font-medium text-muted-foreground text-lg">No printers found</p>
              </div>
            </div>
          ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 hover:bg-muted/50">
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Connection</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton className="h-4 w-[150px] mb-2" /><Skeleton className="h-3 w-[100px]" /></TableCell>
                    <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-[120px]" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-8 rounded-full" /></TableCell>
                  </TableRow>
                ))
              ) : (
                printers.map((printer) => (
                  <TableRow
                    key={printer.id}
                    className="cursor-pointer hover:bg-muted/50 data-[state=selected]:bg-muted data-[state=selected]:hover:bg-muted/80 group"
                    data-state={effectiveSelectedId === printer.id ? 'selected' : undefined}
                    onClick={() => effectiveSelectedId === printer.id ? closeSelectedPrinter() : setSelectedId(printer.id)}
                  >
                    <TableCell>
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {printer.isDefault && <Star className="h-3.5 w-3.5 fill-primary text-primary shrink-0" />}
                        {printer.name}
                        <PrinterStatusIndicator status={printer.status} isActive={printer.isActive} />
                      </div>
                      {(printer.identifier || printer.address) && (
                        <div className="text-xs text-muted-foreground font-mono mt-1">
                          {printer.identifier || printer.address}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <PrinterTypeBadge type={printer.type} />
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{printer.connectionType}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate max-w-[150px] block" title={printer.printAgent?.name || agentMap.get(printer.agentId) || ''}>
                        {printer.printAgent?.name || agentMap.get(printer.agentId) || '—'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity" onClick={(e) => e.stopPropagation()}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation()
                              testMutation.mutate(printer.id)
                            }}
                            disabled={testMutation.isPending && testingPrinterId === printer.id}
                          >
                            <PrinterIcon className="h-4 w-4" /> Test Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditingPrinter(printer)}>
                            <Pencil className="h-4 w-4" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive focus:text-destructive focus:bg-destructive/10" onClick={(e) => {
                            e.stopPropagation()
                            setDeletingPrinter(printer)
                          }}>
                            <Trash2 className="h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          )}

          {!isLoading && printers.length > 0 && (
            <DataTablePagination
              page={meta.page}
              totalPages={meta.totalPages}
              itemsPerPage={meta.limit}
              totalItems={meta.total}
              isLoading={isLoading}
              entityName="printers"
              onPageChange={(p) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
              onLimitChange={(l) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
            />
          )}
        </div>

        {selectedPrinter && (
          <PrinterDetailCard
            printer={selectedPrinter}
            agentName={agentMap.get(selectedPrinter.agentId)}
            onClose={closeSelectedPrinter}
            onEdit={() => setEditingPrinter(selectedPrinter)}
            onDelete={() => setDeletingPrinter(selectedPrinter)}
            onTestPrint={() => testMutation.mutate(selectedPrinter.id)}
          />
        )}
      </div>

      <React.Suspense fallback={null}>
        {editingPrinter && (
          <CreatePrinterDialog
            isOpen={!!editingPrinter}
            onClose={() => {
              setEditingPrinter(null)
            }}
            printer={editingPrinter}
          />
        )}
        
        {deletingPrinter && (
          <DeletePrinterDialog
            isOpen={!!deletingPrinter}
            onClose={() => setDeletingPrinter(null)}
            printer={deletingPrinter}
            onSuccess={() => {
              if (effectiveSelectedId === deletingPrinter.id) {
                closeSelectedPrinter()
              }
            }}
          />
        )}
      </React.Suspense>
    </div>
  )
}
